import { Fragment } from 'react';
import {
    PiClockBold,
    PiPackageBold,
    PiShoppingCartBold,
    PiTruckBold,
    PiWarningBold,
} from 'react-icons/pi';
import { cn } from '@v2/utils/cn';
import { fillBarColor, slotCardClasses, slotCardClassesSolid, slotStatus } from './cd-slot-styles';

export type CdSlot = {
    code: string;
    occupied: boolean;
    count: number;
    value_brl: number;
    missing: boolean;
    cliente_count: number;
    estoque_count: number;
    occupancy_kind: 'free' | 'cliente' | 'estoque' | 'mixed';
    days_label: string | null;
    overdue_count: number;
    overdue_charge_brl: number;
    overdue_max_days: number;
    fill_pct?: number;
    capacity_units?: number;
    primary_product_name?: string | null;
    primary_product_code?: string | null;
    primary_product_id?: number | null;
    products_count?: number;
    orders_count?: number;
    primary_order_number?: string | null;
    total_weight_kg?: number;
    last_movement_label?: string | null;
    slot_status?: string;
    short_code?: string | null;
};

export type CdRuaGrid = {
    code: string;
    label: string;
    grid: CdSlot[][];
    bay_labels: string[];
    level_labels: string[];
    highlight_codes?: string[];
};

type Props = {
    rua: CdRuaGrid;
    variant?: 'compact' | 'detailed';
    compact?: boolean;
    solidColors?: boolean;
    highlightCodes?: string[];
    selectedCode?: string | null;
    onSlotClick?: (slot: CdSlot) => void;
};

function slotIcon(slot: CdSlot) {
    const st = slotStatus(slot);
    if (st === 'overdue') return <PiWarningBold className="size-3 shrink-0" aria-hidden />;
    if (st === 'reserved') return <PiShoppingCartBold className="size-3 shrink-0 opacity-70" aria-hidden />;
    if (!slot.occupied) return null;
    if (slot.estoque_count > 0 && slot.cliente_count === 0) return <PiPackageBold className="size-3 shrink-0 opacity-70" aria-hidden />;
    return <PiTruckBold className="size-3 shrink-0 opacity-70" aria-hidden />;
}

function CdSlotCard({
    slot,
    highlighted,
    selected,
    detailed,
    solidColors,
    onClick,
}: {
    slot: CdSlot;
    highlighted: boolean;
    selected: boolean;
    detailed: boolean;
    solidColors: boolean;
    onClick?: () => void;
}) {
    const clickable = !slot.missing && onClick;
    const Tag = clickable ? 'button' : 'div';
    const fillPct = slot.occupied ? (slot.fill_pct ?? 0) : 0;
    // short_code ("A-C-01") não tem o nível: os 3 andares da baia ficavam com o mesmo código.
    const displayCode = slot.code.split('-').slice(1).join('-') || slot.short_code || slot.code;
    const productLabel = slot.primary_product_name
        ? slot.primary_product_name.length > 22
            ? `${slot.primary_product_name.slice(0, 20)}…`
            : slot.primary_product_name
        : null;

    const cardClasses = solidColors
        ? slotCardClassesSolid(slot, highlighted)
        : slotCardClasses(slot, highlighted);

    return (
        <Tag
            type={clickable ? 'button' : undefined}
            onClick={clickable ? onClick : undefined}
            disabled={slot.missing}
            title={`${slot.code}${slot.primary_product_name ? ' · ' + slot.primary_product_name : ''}`}
            className={cn(
                'flex w-full flex-col rounded-lg border transition-all',
                detailed
                    ? 'min-h-[5.5rem] px-2.5 py-2'
                    : 'min-h-[2.75rem] items-center justify-center px-1 py-1.5 text-center',
                clickable && 'hover:z-[1] hover:brightness-110 hover:shadow-lg',
                'disabled:cursor-not-allowed',
                cardClasses,
                highlighted && 'cd-slot-pulse border-amber-500 bg-amber-100 text-amber-950 dark:border-[#C9A84C] dark:bg-[#C9A84C]/30 dark:text-[#F0D06A]',
                selected && !highlighted && 'ring-2 ring-blue-500 ring-offset-1 ring-offset-zinc-950',
                selected && highlighted && 'ring-2 ring-amber-600 ring-offset-1 dark:ring-[#C9A84C]',
            )}
        >
            {/* Código do slot */}
            <div className={cn('flex w-full items-start justify-between gap-1', !detailed && 'justify-center')}>
                <span className="font-mono text-[10px] font-black leading-none tracking-wide">
                    {displayCode}
                </span>
                {detailed && slotIcon(slot)}
            </div>

            {/* Detalhes (modo detailed) */}
            {detailed && slot.occupied && (
                <>
                    <div className="mt-1 flex w-full items-baseline justify-between gap-1 text-[9px] font-bold opacity-90">
                        <span>
                            {slot.count} un.
                            {fillPct > 0 && <span className="opacity-70"> · {fillPct}%</span>}
                        </span>
                    </div>
                    {productLabel && (
                        <p className="mt-0.5 line-clamp-2 w-full text-[9px] font-medium leading-tight opacity-85">
                            {productLabel}
                        </p>
                    )}
                    {slot.primary_order_number && (
                        <p className="mt-0.5 w-full truncate text-[8px] font-black uppercase tracking-wide opacity-75">
                            Ped: {slot.primary_order_number}
                        </p>
                    )}
                    {slot.days_label && (
                        <p className="mt-0.5 flex items-center gap-0.5 text-[8px] opacity-65">
                            <PiClockBold className="size-2.5" />
                            {slot.days_label}
                        </p>
                    )}
                    {/* Barra de ocupação */}
                    <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-black/20">
                        <div
                            className={cn('h-full rounded-full transition-all', fillBarColor(fillPct))}
                            style={{ width: `${Math.max(fillPct, slot.occupied ? 8 : 0)}%` }}
                        />
                    </div>
                </>
            )}

            {/* Compact: mostra só contagem */}
            {!detailed && (
                <span className="mt-0.5 text-[9px] font-bold opacity-85">
                    {slot.count > 0 ? `${slot.count} un.` : 'livre'}
                </span>
            )}

            {!detailed && slot.overdue_count > 0 && (
                <PiWarningBold className="mt-0.5 size-3" />
            )}
        </Tag>
    );
}

/* ── Legenda ────────────────────────────────────────────────── */
function Legend() {
    const items = [
        { label: 'Livre',    dot: 'bg-emerald-500' },
        { label: 'Estoque',  dot: 'bg-gray-400 dark:bg-zinc-500' },
        { label: 'Cliente',  dot: 'bg-sky-500' },
        { label: 'Misto',    dot: 'bg-violet-500' },
        { label: 'Vencido',  dot: 'bg-red-500' },
        { label: 'Destaque', dot: 'bg-amber-500' },
    ];
    return (
        <div className="mt-3 flex flex-wrap items-center gap-3">
            {items.map(({ label, dot }) => (
                <span key={label} className="flex items-center gap-1.5 text-[10px] text-gray-500 dark:text-zinc-500">
                    <span className={cn('size-2 rounded-full', dot)} />
                    {label}
                </span>
            ))}
        </div>
    );
}

/* ── Separador entre andares (prateleira física) ─────────────── */
function ShelfDivider({ cols }: { cols: number }) {
    return (
        <tr aria-hidden>
            <td colSpan={cols + 1} className="px-1 py-1">
                {/* Light mode: barra aço escovado */}
                <div
                    className="block h-1.5 w-full rounded-sm dark:hidden"
                    style={{
                        background: 'linear-gradient(90deg, #d1d5db 0%, #9ca3af 20%, #6b7280 50%, #9ca3af 80%, #d1d5db 100%)',
                        boxShadow: '0 2px 3px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.5)',
                    }}
                />
                {/* Dark mode: barra dourada metálica */}
                <div
                    className="hidden h-1.5 w-full rounded-sm dark:block"
                    style={{
                        background: 'linear-gradient(90deg, #3D2D08 0%, #8B6914 15%, #C9A84C 40%, #8B6914 65%, #3D2D08 100%)',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.08)',
                    }}
                />
            </td>
        </tr>
    );
}

/* ── Componente principal ────────────────────────────────────── */
export default function CdRackGrid({
    rua,
    variant,
    compact = false,
    solidColors = false,
    highlightCodes = [],
    selectedCode = null,
    onSlotClick,
}: Props) {
    const detailed = variant === 'detailed' || (!compact && variant !== 'compact');
    const highlights = new Set([...(rua.highlight_codes ?? []), ...highlightCodes]);

    return (
        <div className="overflow-x-auto pb-1">
            <div
                className={cn(
                    'inline-block min-w-full rounded-2xl border shadow-inner',
                    'border-gray-200 bg-gray-50/80',
                    'dark:border-zinc-800 dark:bg-zinc-900/60',
                    detailed ? 'p-4' : 'p-2',
                )}
            >
                <table className="w-full min-w-[480px] border-collapse">
                    <thead>
                        <tr>
                            <th className="w-20 pb-2" />
                            {rua.bay_labels.map((bl) => (
                                <th
                                    key={bl}
                                    className="px-1 pb-2 text-center text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-600"
                                >
                                    Baia {bl}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rua.grid.map((row, ri) => (
                            <Fragment key={ri}>
                                {ri > 0 && <ShelfDivider cols={rua.bay_labels.length} />}

                                <tr>
                                    <th className="whitespace-nowrap px-3 py-1 text-right text-[10px] font-black text-gray-400 dark:text-zinc-500">
                                        {rua.level_labels[ri] ?? `Nv ${rua.grid.length - ri}`}
                                    </th>
                                    {row.map((slot) => (
                                        <td key={slot.code} className="p-1 align-top">
                                            <CdSlotCard
                                                slot={slot}
                                                highlighted={highlights.has(slot.code)}
                                                selected={selectedCode === slot.code}
                                                detailed={detailed}
                                                solidColors={solidColors}
                                                onClick={
                                                    !slot.missing && onSlotClick
                                                        ? () => onSlotClick(slot)
                                                        : undefined
                                                }
                                            />
                                        </td>
                                    ))}
                                </tr>
                            </Fragment>
                        ))}
                    </tbody>
                </table>

                {/* Legenda */}
                <Legend />
            </div>
        </div>
    );
}
